// Gradient Builder - Main Modal Component
// Two-column layout: 40% preview+filter+code left, 60% tabbed controls right

import React, { useState, useRef, useEffect } from "react";
import { LuX, LuCopy, LuCircleHelp, LuChevronDown } from "react-icons/lu";
import { GradientConfig, GradientPreset, GradientType, ColorStop } from "../../model/gradient";
import { generateFlutterCode } from "../../utils/gradientCodeGenerator";
import { GradientPreviewCanvas } from "./gradient/GradientPreviewCanvas";
import { GradientPresetGallery } from "./gradient/GradientPresetGallery";
import { ColorStopEditor } from "./gradient/ColorStopEditor";
import { GradientTypeControls } from "./gradient/GradientTypeControls";
import { gradientPresets } from "../../data/gradientPresets";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { colors, typography, effects, components } from "../../design/tokens";

// Tab type for the right column
type TabId = 'presets' | 'colors' | 'settings';

const tabs: { id: TabId; label: string }[] = [
  { id: 'presets', label: 'Presets' },
  { id: 'colors', label: 'Colors' },
  { id: 'settings', label: 'Settings' },
];

interface GradientBuilderModalProps {
  onClose: () => void;
  onInsertToPrompt?: (code: string) => void;
}

export const GradientBuilderModal: React.FC<GradientBuilderModalProps> = ({
  onClose,
  onInsertToPrompt,
}) => {
  const isCompactView = useIsCompactView();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { showSnackbar } = useSnackbar();

  // Start with Purple Midnight preset (index 2)
  const [selectedConfig, setSelectedConfig] = useState<GradientConfig>(
    gradientPresets[2].config
  );
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(
    gradientPresets[2].id
  );

  // Tab state - Presets is the default tab
  const [activeTab, setActiveTab] = useState<TabId>('presets');

  // Flutter Code help modal state
  const [showFlutterCodeHelpModal, setShowFlutterCodeHelpModal] = useState(false);

  // Mobile Flutter Code section expansion state
  const [isMobileCodeExpanded, setIsMobileCodeExpanded] = useState(false);

  // Track color validation state
  const [colorsValid, setColorsValid] = useState(true);

  // Handle ESC key to close modal (closes help modal first if open)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showFlutterCodeHelpModal) {
          setShowFlutterCodeHelpModal(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showFlutterCodeHelpModal, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  // Handle preset selection
  const handlePresetSelect = (preset: GradientPreset) => {
    // Deep clone the config to ensure React detects the change
    const clonedColors = preset.config.colors.map(c => ({ ...c }));
    setSelectedConfig({ ...preset.config, colors: clonedColors });
    setSelectedPresetId(preset.id);
  };

  // Handle gradient type change
  const handleTypeChange = (newType: GradientType) => {
    if (newType === selectedConfig.type) return;

    // Keep colors, change type
    const colors = selectedConfig.colors;

    if (newType === "linear") {
      setSelectedConfig({
        type: "linear",
        colors,
        angle: 135,
      });
    } else if (newType === "radial") {
      setSelectedConfig({
        type: "radial",
        colors,
        center: "center",
        radius: 1.2,
      });
    } else if (newType === "sweep") {
      setSelectedConfig({
        type: "sweep",
        colors,
        center: "center",
        startAngle: 0,
        endAngle: 6.28,
      });
    }

    setSelectedPresetId(null); // Clear preset selection when customizing
  };

  // Handle color stops change
  const handleColorStopsChange = (newColors: ColorStop[]) => {
    setSelectedConfig({
      ...selectedConfig,
      colors: newColors,
    });
    setSelectedPresetId(null); // Clear preset selection when customizing
  };

  // Handle config change
  const handleConfigChange = (newConfig: GradientConfig) => {
    setSelectedConfig(newConfig);
    setSelectedPresetId(null); // Clear preset selection when customizing
  };

  // Copy Flutter code to clipboard
  const handleCopyCode = async () => {
    const flutterCode = generateFlutterCode(selectedConfig);
    try {
      await navigator.clipboard.writeText(flutterCode);
      showSnackbar("Flutter code copied!", { type: "success" });
    } catch {
      showSnackbar("Failed to copy code", { type: "error" });
    }
  };

  // Insert Flutter code to chat prompt (wrapped in markdown code block for LLM clarity)
  const handleUseInChat = () => {
    const flutterCode = generateFlutterCode(selectedConfig);
    const markdownWrapped = "```dart\n" + flutterCode + "\n```";
    onInsertToPrompt?.(markdownWrapped);
  };

  // Generate Flutter code for display
  const flutterCode = generateFlutterCode(selectedConfig);

  return (
    <>
      {/* Flutter Code Help Modal */}
      {showFlutterCodeHelpModal && (
        <div
          className={`fixed inset-0 z-[60] flex items-center justify-center ${colors.overlay.medium} p-4`}
          onClick={() => setShowFlutterCodeHelpModal(false)}
        >
          <div
            className={`relative w-full max-w-md ${colors.surface.primary} border ${colors.border.primary} ${effects.borderRadius.xl} p-6`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className={`absolute top-4 right-4 ${components.modal.closeButton}`}
              onClick={() => setShowFlutterCodeHelpModal(false)}
            >
              <LuX size={20} />
            </button>

            {/* Modal content */}
            <div className="flex flex-col gap-4">
              <h3 className={`${typography.size.lg} ${typography.weight.semibold} ${colors.text.primary}`}>
                How to Use This Gradient Code
              </h3>
              <div className={`flex flex-col gap-3 ${colors.text.secondary}`}>
                <div className="flex flex-col gap-1">
                  <span className={`${typography.weight.semibold} ${colors.text.primary}`}>
                    Quick Insert
                  </span>
                  <span className={typography.size.sm}>
                    Click the "Insert" button to instantly add the gradient code to your chat prompt.
                  </span>
                </div>

                <div className="flex flex-col gap-1">
                  <span className={`${typography.weight.semibold} ${colors.text.primary}`}>
                    Or Copy & Paste
                  </span>
                  <span className={typography.size.sm}>
                    Click the copy icon to copy the Flutter code to your clipboard, then paste it into your project chat.
                  </span>
                </div>

                <div className="flex flex-col gap-1">
                  <span className={`${typography.weight.semibold} ${colors.text.primary}`}>
                    Tell the AI What to Do
                  </span>
                  <span className={typography.size.sm}>
                    Add instructions like "Apply this gradient to the app bar background" or "Use this gradient for all primary buttons".
                  </span>
                </div>

                <div className="flex flex-col gap-1">
                  <span className={`${typography.weight.semibold} ${colors.text.primary}`}>
                    Example Use Cases
                  </span>
                  <span className={typography.size.sm}>
                    • App backgrounds and scaffolds<br/>
                    • Button backgrounds<br/>
                    • Card and container decorations<br/>
                    • Text gradients (with shader masks)<br/>
                    • Hero sections and banners
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: Single-column layout (stacked vertically) */}
      {isCompactView ? (
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center ${colors.overlay.default} backdrop-blur-sm`}
        onClick={handleBackdropClick}
      >
        <div
          ref={modalContentRef}
          className={`relative ${colors.surface.primary} w-full h-full flex flex-col`}
        >
          {/* Header */}
          <div className={`flex-shrink-0 ${colors.surface.primary} border-b ${colors.border.primary} px-6 py-4 flex items-center justify-between`}>
            <h2 className={`${typography.size['2xl']} ${typography.weight.bold} ${colors.text.primary}`}>
              Gradient Builder
            </h2>
            <button
              onClick={onClose}
              className={components.modal.closeButton}
            >
              <LuX size={24} />
            </button>
          </div>

          {/* Preview - compact height for mobile */}
          <div className={`flex-shrink-0 h-[120px] border-b ${colors.border.primary}`}>
            <GradientPreviewCanvas config={selectedConfig} className="rounded-none" />
          </div>

          {/* Gradient Type Filter - Mobile: Subtle segmented control */}
          <div className={`flex-shrink-0 px-4 py-3 border-b ${colors.border.primary}`}>
            <div className={`inline-flex ${colors.surface.secondary}/50 ${effects.borderRadius.lg} p-0.5`}>
              {(["linear", "radial", "sweep"] as GradientType[]).map((type) => (
                <button
                  key={type}
                  onClick={() => handleTypeChange(type)}
                  className={`
                    px-4 py-1.5 ${typography.size.xs} ${typography.weight.medium} ${effects.borderRadius.md} ${effects.transition.default} ${typography.transform.uppercase} ${typography.tracking.wider}
                    ${
                      selectedConfig.type === type
                        ? `${colors.surface.primary} ${colors.text.primary}`
                        : `text-dark-text-muted hover:text-dark-text`
                    }
                  `}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Bar - Mobile: Text-based navigation with underline indicator */}
          <div className={`flex-shrink-0 flex border-b ${colors.border.primary}`}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-4 py-3 ${typography.size.sm} ${typography.weight.medium} transition-colors relative
                  ${activeTab === tab.id
                    ? colors.text.primary
                    : `${colors.text.secondary} hover:text-dark-text`
                  }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <span className={`absolute bottom-0 left-4 right-4 h-0.5 ${colors.surface.primary} bg-dark-text ${effects.borderRadius.full}`} />
                )}
              </button>
            ))}
          </div>

          {/* Tab Content - Mobile (scrollable, all tabs rendered to preserve scroll) */}
          <div className="flex-1 overflow-hidden relative">
            <div className={`absolute inset-0 overflow-y-auto p-4 ${activeTab === 'presets' ? '' : 'hidden'}`}>
              <GradientPresetGallery
                onSelectPreset={handlePresetSelect}
                selectedPresetId={selectedPresetId ?? undefined}
                columns={2}
              />
            </div>
            <div className={`absolute inset-0 overflow-y-auto p-4 ${activeTab === 'colors' ? '' : 'hidden'}`}>
              <ColorStopEditor
                colorStops={selectedConfig.colors}
                onChange={handleColorStopsChange}
                onValidationChange={setColorsValid}
              />
            </div>
            <div className={`absolute inset-0 overflow-y-auto p-4 ${activeTab === 'settings' ? '' : 'hidden'}`}>
              <GradientTypeControls
                config={selectedConfig}
                onChange={handleConfigChange}
              />
            </div>
          </div>

          {/* Flutter Code - Collapsible Bottom Section */}
          <div className={`flex-shrink-0 border-t ${colors.border.primary} ${colors.surface.secondary} touch-none`}>
            {/* Header row with label, help, expand toggle, and action buttons */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsMobileCodeExpanded(!isMobileCodeExpanded)}
                  className={`flex items-center gap-2 ${typography.size.sm} ${colors.text.secondary} ${typography.weight.medium} hover:text-dark-text transition-colors`}
                >
                  <span>Flutter Code</span>
                  <LuChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${isMobileCodeExpanded ? 'rotate-180' : ''}`}
                  />
                </button>
                <LuCircleHelp
                  size={16}
                  onClick={() => setShowFlutterCodeHelpModal(true)}
                  className={`${colors.text.secondary} hover:text-dark-text cursor-pointer transition-colors`}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCopyCode}
                  className={`p-2 ${effects.borderRadius.lg} ${effects.transition.default} ${colors.surface.primary} border ${colors.border.primary}
                    ${colors.text.secondary} hover:text-dark-text hover:border-dark-text-secondary`}
                  title="Copy to clipboard"
                >
                  <LuCopy size={18} />
                </button>
                {onInsertToPrompt && (
                  <button
                    onClick={handleUseInChat}
                    disabled={!colorsValid}
                    className={`px-6 py-2 ${effects.borderRadius.full} ${effects.transition.default} bg-primary-blue text-white ${typography.size.sm} ${typography.weight.medium} ${colorsValid ? colors.hover.opacity : "opacity-50 cursor-not-allowed"}`}
                  >
                    Insert
                  </button>
                )}
              </div>
            </div>
            {/* Collapsible code preview */}
            {isMobileCodeExpanded && (
              <div className="px-4 pb-4">
                <pre className={`${typography.size.xs} ${colors.text.primary} ${colors.surface.primary} p-3 ${effects.borderRadius.lg} border ${colors.border.primary} overflow-x-auto max-h-[150px] overflow-y-auto`}>
                  {flutterCode}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
      ) : (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${colors.overlay.default} backdrop-blur-sm`}
      onClick={handleBackdropClick}
    >
      <div
        ref={modalContentRef}
        className={`relative ${colors.surface.primary} ${effects.borderRadius.xl} ${effects.shadow.xl} w-[891px] h-[700px] max-h-[90vh] m-6 flex flex-col`}
      >
        {/* Header */}
        <div className={`flex-shrink-0 ${colors.surface.primary} border-b ${colors.border.primary} px-6 py-4 flex items-center justify-between`}>
          <h2 className={`${typography.size['2xl']} ${typography.weight.bold} ${colors.text.primary}`}>
            Gradient Builder
          </h2>
          <button
            onClick={onClose}
            className={components.modal.closeButton}
          >
            <LuX size={24} />
          </button>
        </div>

        {/* Two-column layout */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Left Column (40%): Preview + Type Filter + Code */}
          <div className={`w-[40%] border-r ${colors.border.primary} flex flex-col min-h-0`}>
            {/* Preview - 45% of column height */}
            <div className="h-[45%] p-6 min-h-0">
              <GradientPreviewCanvas config={selectedConfig} />
            </div>

            {/* Gradient Type Filter - Desktop: Subtle segmented control */}
            <div className={`flex-shrink-0 px-4 py-3 border-t ${colors.border.primary}`}>
              <div className={`inline-flex ${colors.surface.secondary}/50 ${effects.borderRadius.lg} p-0.5`}>
                {(["linear", "radial", "sweep"] as GradientType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => handleTypeChange(type)}
                    className={`
                      px-4 py-1.5 ${typography.size.xs} ${typography.weight.medium} ${effects.borderRadius.md} ${effects.transition.default} ${typography.transform.uppercase} ${typography.tracking.wider}
                      ${
                        selectedConfig.type === type
                          ? `${colors.surface.primary} ${colors.text.primary}`
                          : `text-dark-text-muted hover:text-dark-text`
                      }
                    `}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Flutter Code - remaining space */}
            <div className={`flex-1 border-t ${colors.border.primary} ${colors.surface.secondary} p-4 flex flex-col min-h-0`}>
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <label className={`${typography.size.sm} ${colors.text.secondary} ${typography.weight.medium}`}>
                    Flutter Code
                  </label>
                  <LuCircleHelp
                    size={16}
                    onClick={() => setShowFlutterCodeHelpModal(true)}
                    className={`${colors.text.secondary} hover:text-dark-text cursor-pointer transition-colors`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCopyCode}
                    className={`p-1.5 ${effects.borderRadius.default} ${effects.transition.default}
                      ${colors.text.secondary} hover:text-dark-text hover:bg-dark-surface-tertiary`}
                    title="Copy to clipboard"
                  >
                    <LuCopy size={18} />
                  </button>
                  {onInsertToPrompt && (
                    <button
                      onClick={handleUseInChat}
                      disabled={!colorsValid}
                      className={`px-5 py-1.5 ${effects.borderRadius.full} ${typography.size.sm} ${typography.weight.medium} ${effects.transition.default} bg-primary-blue text-white ${colorsValid ? colors.hover.opacity : "opacity-50 cursor-not-allowed"}`}
                    >
                      Insert
                    </button>
                  )}
                </div>
              </div>
              <pre className={`${typography.size.xs} ${colors.text.primary} ${colors.surface.primary} p-3 ${effects.borderRadius.lg} border ${colors.border.primary} overflow-x-auto flex-1 overflow-y-auto`}>
                {flutterCode}
              </pre>
            </div>
          </div>

          {/* Right Column (60%): Tabs + Content */}
          <div className="w-[60%] flex flex-col overflow-hidden min-h-0">
            {/* Tab Bar - Desktop: Text-based navigation with underline indicator */}
            <div className={`flex-shrink-0 flex border-b ${colors.border.primary}`}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-4 py-3 ${typography.size.sm} ${typography.weight.medium} transition-colors relative
                    ${activeTab === tab.id
                      ? colors.text.primary
                      : `${colors.text.secondary} hover:text-dark-text`
                    }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <span className={`absolute bottom-0 left-4 right-4 h-0.5 bg-dark-text ${effects.borderRadius.full}`} />
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content - All tabs rendered to preserve scroll position */}
            <div className="flex-1 overflow-hidden relative">
              <div className={`absolute inset-0 overflow-y-auto p-6 ${activeTab === 'presets' ? '' : 'hidden'}`}>
                <GradientPresetGallery
                  onSelectPreset={handlePresetSelect}
                  selectedPresetId={selectedPresetId ?? undefined}
                  columns={2}
                />
              </div>
              <div className={`absolute inset-0 overflow-y-auto p-6 ${activeTab === 'colors' ? '' : 'hidden'}`}>
                <ColorStopEditor
                  colorStops={selectedConfig.colors}
                  onChange={handleColorStopsChange}
                  onValidationChange={setColorsValid}
                />
              </div>
              <div className={`absolute inset-0 overflow-y-auto p-6 ${activeTab === 'settings' ? '' : 'hidden'}`}>
                <GradientTypeControls
                  config={selectedConfig}
                  onChange={handleConfigChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
      )}
    </>
  );
};
